/**
 * Plugin Builder editor keyboard shortcuts.
 *
 * Handles three bindings:
 *   - Ctrl/Cmd + A: scope selection to the currently-focused pane
 *     (conversation or preview) instead of the whole document
 *   - Ctrl/Cmd + Z: undo
 *   - Ctrl/Cmd + Shift + Z / Ctrl/Cmd + Y: redo
 *
 * All shortcuts are suppressed when the target is an INPUT or TEXTAREA so
 * the user can still select/undo inside form fields.
 *
 * Extracted from PluginBuilderPage.tsx during PR 12.
 */

import { useEffect, MutableRefObject } from 'react';

export interface UseEditorKeyboardShortcutsOptions {
  undo: () => void;
  redo: () => void;
  /** Ref to the pane that was last clicked; used to scope Ctrl+A selection. */
  activePaneRef: MutableRefObject<HTMLDivElement | null>;
}

export function useEditorKeyboardShortcuts({
  undo,
  redo,
  activePaneRef,
}: UseEditorKeyboardShortcutsOptions): void {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + A: scope selection to the active pane
      if (modifier && e.key.toLowerCase() === 'a') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        if (activePaneRef.current) {
          e.preventDefault();
          const selection = window.getSelection();
          if (selection) {
            const range = document.createRange();
            range.selectNodeContents(activePaneRef.current);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }

      // Ctrl/Cmd + Z (undo) or Ctrl/Cmd + Shift + Z (redo)
      if (modifier && e.key.toLowerCase() === 'z') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }

      // Ctrl/Cmd + Y for redo (Windows convention)
      if (modifier && e.key.toLowerCase() === 'y') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, activePaneRef]);
}
